import React, { useEffect, useState, useRef, useCallback } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import { Particles } from '../components/Particles.tsx';
import { 
    Box, Typography, Button, useMediaQuery, useTheme, 
    DialogContent, Zoom, styled, keyframes, Dialog, DialogTitle, Alert, Stack, DialogActions 
} from '@mui/material';
import { FaHeart, FaEye, FaPlusCircle, FaArrowLeft } from 'react-icons/fa';
import ValentinCardCreator from '../components/ValentinCardCreator.tsx';

// --- STYLED COMPONENTS ---

const backgroundMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AnimatedDialog = styled(Dialog, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
  })<{ darkMode: boolean }>(({ darkMode, theme }) => ({
    '& .MuiPaper-root': {
      borderRadius: '16px',
      border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(255, 215, 0, 0.3)',
      background: darkMode 
          ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
          : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
      backgroundSize: '200% 200%',
      animation: `${backgroundMove} 12s ease infinite`,
      color: 'white',
      padding: theme.spacing(1),
    },
    '& .MuiDialogTitle-root': {
      fontWeight: 'bold',
      textAlign: 'center',
      fontSize: '1.4rem',
    },
    '& .MuiDialogContent-root': {
      color: 'rgba(255, 255, 255, 0.9)',
    }
}));

const DialogActionButton = styled(Button)(() => ({
    borderRadius: '20px',
    textTransform: 'none',
    padding: '12px 24px',
    fontWeight: 'bold',
    fontSize: '1rem',
    color: 'white',
    backgroundColor: '#ff4d6d',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    width: '100%',
    display: 'flex',
    gap: '10px',
    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    '&:hover': {
        backgroundColor: '#ff758f',
        transform: 'translateY(-3px) scale(1.02)',
        boxShadow: '0 6px 20px rgba(255, 77, 109, 0.5)',
    }
}));

// --- ANIMÁCIÓK ---
const fadeCycle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.8) rotate(-5deg); }
  50% { opacity: 0.8; transform: scale(1) rotate(5deg); }
`;

const GIF_DATA = {
    question: ["15502118324612616342", "12053814847638028030", "25676985", "26097141", "24985324", "24985048"],
    angry: ["20195177", "18330252195217648656", "21681908", "14967220799009428095", "9323741479407494404"],
    love: ["481671407995970679", "21458020", "21655580", "15123231842779869542", "20721043"]
};

// --- FŐ KOMPONENS ---
const ValentinCard: React.FC = () => {
    const isAuthenticated = !!Cookies.get('token');
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const darkMode = true; 

    // Állapotok
    const [isStarted, setIsStarted] = useState(false); 
    const [showCreator, setShowCreator] = useState(false); 
    const [loginPromptOpen, setLoginPromptOpen] = useState(false);
    
    const [phase, setPhase] = useState<'question' | 'angry' | 'love'>('question');
    const [step, setStep] = useState(0);
    const [noBtnPos, setNoBtnPos] = useState({ left: '60%', top: '60%' });
    const [yesBtnPos, setYesBtnPos] = useState({ left: '30%', top: '60%' });
    const [floatingGifs, setFloatingGifs] = useState<{ id: string, x: number, y: number, key: number }[]>([]);
    const noBtnRef = useRef<HTMLButtonElement>(null);
    const angryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // --- Create Button Handler ---
    const handleCreateClick = () => {
        if (isAuthenticated) {
            setShowCreator(true);
        } else {
            setLoginPromptOpen(true);
        }
    };

    // --- Tenor Script kezelése ---
    useEffect(() => {
        if (!isStarted) return; 
        const script = document.createElement("script");
        script.src = "https://tenor.com/embed.js";
        script.async = true;
        document.body.appendChild(script);
        return () => { 
            const existingScript = document.querySelector('script[src*="tenor.com/embed.js"]');
            if (existingScript) document.body.removeChild(existingScript);
        };
    }, [isStarted, phase, floatingGifs]);

    // --- GIF Generálás ---
    const generateGifs = useCallback((currentPhase: 'question' | 'angry' | 'love') => {
        if (!isStarted) return;
        const newGifs = [];
        const library = GIF_DATA[currentPhase];
        const gifCount = isMobile ? 4 : 8;
        const size = isMobile ? 120 : 180;

        for (let i = 0; i < gifCount; i++) {
            newGifs.push({
                id: library[i % library.length],
                x: Math.random() * (window.innerWidth - size - 40) + 20,
                y: Math.random() * (window.innerHeight - size - 100) + 50,
                key: Math.random()
            });
        }
        setFloatingGifs(newGifs);
    }, [isMobile, isStarted]);

    useEffect(() => {
        if (isStarted) {
            generateGifs(phase);
            const interval = setInterval(() => generateGifs(phase), 8000);
            return () => clearInterval(interval);
        }
    }, [isStarted, phase, generateGifs]);

    // --- Menekülő Logika ---
    const handleNoMove = useCallback(() => {
        if (phase === 'love' || !isStarted) return;
        if (angryTimeoutRef.current) clearTimeout(angryTimeoutRef.current);
        
        const btnWidth = isMobile ? 110 : 140;
        const btnHeight = 50;
        const maxX = window.innerWidth - btnWidth - 20;
        const maxY = window.innerHeight - btnHeight - 20;
        
        const nx = Math.max(20, Math.random() * maxX);
        const ny = Math.max(150, Math.random() * maxY);

        setNoBtnPos({ left: `${nx}px`, top: `${ny}px` });
        setPhase('angry');

        angryTimeoutRef.current = setTimeout(() => {
            setPhase('question');
        }, 3000);
    }, [phase, isStarted, isMobile]);

    useEffect(() => {
        const btn = noBtnRef.current;
        if (btn && isStarted) {
            const handleTouch = (e: TouchEvent) => {
                e.preventDefault();
                handleNoMove();
            };
            btn.addEventListener('touchstart', handleTouch, { passive: false });
            return () => btn.removeEventListener('touchstart', handleTouch);
        }
    }, [isStarted, handleNoMove]);

    useEffect(() => {
        if (isStarted) {
            const startY = window.innerHeight * 0.6;
            const centerX = window.innerWidth / 2;
            if (isMobile) {
                setYesBtnPos({ left: `${centerX - 120}px`, top: `${startY}px` });
                setNoBtnPos({ left: `${centerX + 10}px`, top: `${startY}px` });
            } else {
                setYesBtnPos({ left: `${centerX - 180}px`, top: `${startY}px` });
                setNoBtnPos({ left: `${centerX + 40}px`, top: `${startY}px` });
            }
        }
    }, [isStarted, isMobile]);

    const handleYesClick = () => {
        if (angryTimeoutRef.current) clearTimeout(angryTimeoutRef.current);
        if (step < 2) {
            setStep(prev => prev + 1);
            setPhase('question');
        } else {
            setPhase('love');
        }
    };

    return (
        <Box sx={{ 
            position: 'relative', 
            minHeight: '100vh', 
            width: '100vw',
            overflow: 'hidden', 
            background: 'linear-gradient(to bottom, #fff0f3, #ffccd5)' 
        }}>
            {/* Kezdő Dialog (Amíg nem indul el a kártya) */}
            {!isStarted && !showCreator && (
                <Box sx={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.85)',
                    backdropFilter: 'blur(10px)'
                }}>
                    <Zoom in={true}>
                        <Box sx={{
                            width: isMobile ? '90%' : '450px',
                            p: 4,
                            borderRadius: '24px',
                            background: 'linear-gradient(45deg, #000000, #4a0512)',
                            border: '2px solid #ff4d6d',
                            textAlign: 'center',
                            boxShadow: '0 0 30px rgba(255, 77, 109, 0.5)'
                        }}>
                            <FaHeart size={60} color="#ff4d6d" style={{ marginBottom: '20px' }} />
                            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 3, fontFamily: "'Dancing Script', cursive" }}>
                                Valentin Card Manager
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <DialogActionButton onClick={() => setIsStarted(true)}>
                                    <FaEye /> Preview Default Card
                                </DialogActionButton>
                                
                                <DialogActionButton 
                                    sx={{ bgcolor: 'transparent', border: '2px solid #ff4d6d', '&:hover': { bgcolor: 'rgba(255, 77, 109, 0.1)' } }}
                                    onClick={handleCreateClick}
                                >
                                    <FaPlusCircle /> Create Own Valentin Card
                                </DialogActionButton>
                                <Button 
                                    onClick={() => window.history.back()} 
                                    sx={{ 
                                        color: 'rgba(255,255,255,0.6)', 
                                        textTransform: 'none',
                                        fontSize: '0.9rem',
                                        mt: 1,
                                        '&:hover': { color: '#ff4d6d', bgcolor: 'transparent' }
                                    }}
                                    startIcon={<FaArrowLeft size={14} />}
                                >
                                    Back
                                </Button>
                            </Box>
                        </Box>
                    </Zoom>
                </Box>
            )}

            {/* Authentication Prompt Dialog */}
            <AnimatedDialog open={loginPromptOpen} onClose={() => setLoginPromptOpen(false)} disableScrollLock darkMode={darkMode}>
                  <DialogTitle>Authentication Required</DialogTitle>
                  <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>You need to be logged in to create a custom card!</Alert>
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <DialogActionButton onClick={() => navigate('/login')}>Sign In</DialogActionButton>
                      <DialogActionButton onClick={() => navigate('/register')}>Sign Up</DialogActionButton>
                    </Stack>
                  </DialogContent>
                  <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button onClick={() => setLoginPromptOpen(false)} sx={{ color: 'white' }}>Maybe later</Button>
                  </DialogActions>
            </AnimatedDialog>

            {/* Creator Dialog Component */}
            <ValentinCardCreator 
                open={showCreator} 
                onClose={() => setShowCreator(false)} 
                darkMode={darkMode} 
            />

            {/* HÁTTÉR */}
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                <Particles />
            </Box>

            {/* NAVIGÁCIÓ */}
            <Box sx={{ position: 'relative', zIndex: 100 }}>
                {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}
            </Box>

            {/* GIF RÉTEG (Csak ha elindult) */}
            {isStarted && (
                <Box sx={{ position: 'fixed', inset: 0, zIndex: 10, pointerEvents: 'none' }}>
                    {floatingGifs.map((gif) => (
                        <Box 
                            key={gif.key}
                            className="tenor-gif-embed" 
                            data-postid={gif.id} 
                            data-share-method="host" 
                            data-aspect-ratio="1" 
                            sx={{
                                position: 'absolute',
                                left: `${gif.x}px`,
                                top: `${gif.y}px`,
                                width: isMobile ? '120px' : '180px',
                                animation: `${fadeCycle} 4s infinite ease-in-out`,
                                borderRadius: '15px',
                                overflow: 'hidden',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                            }}
                        />
                    ))}
                </Box>
            )}

            {/* FŐ TARTALOM (Csak ha elindult) */}
            {isStarted && (
                <Box sx={{ 
                    position: 'relative', 
                    zIndex: 50, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    textAlign: 'center',
                    pt: isMobile ? '20vh' : '15vh'
                }}>
                    <Typography variant="h2" sx={{ 
                        fontSize: isMobile ? '1.8rem' : '3.5rem', 
                        color: '#ff4d6d', 
                        fontWeight: 'bold',
                        maxWidth: '90%',
                        textShadow: '2px 2px 4px rgba(255,255,255,0.5)',
                        fontFamily: "'Dancing Script', cursive"
                    }}>
                        {phase === 'love' ? "💖 I love you!!! 💖" : 
                         step === 0 ? "I have a question..." : 
                         step === 1 ? "💛 Will you be my Valentine, my love?? 💛" : "Are you sure? 🥺"}
                    </Typography>

                    {phase !== 'love' && (
                        <>
                            <Button
                                onClick={handleYesClick}
                                sx={{
                                    position: 'fixed',
                                    left: yesBtnPos.left,
                                    top: yesBtnPos.top,
                                    bgcolor: '#ff4d6d',
                                    color: 'white',
                                    borderRadius: '50px',
                                    px: isMobile ? 3 : 5,
                                    py: 1.5,
                                    fontWeight: 'bold',
                                    fontSize: isMobile ? '0.9rem' : '1.2rem',
                                    zIndex: 60,
                                    transition: 'all 0.3s ease',
                                    '&:hover': { bgcolor: '#ff758f', transform: 'scale(1.1)' }
                                }}
                            >
                                {step === 0 ? "Continue..." : step === 1 ? "Yes, of course!!" : "Yeeeees!!"}
                            </Button>

                            <Button
                                ref={noBtnRef}
                                onMouseEnter={handleNoMove}
                                sx={{
                                    position: 'fixed',
                                    left: noBtnPos.left,
                                    top: noBtnPos.top,
                                    bgcolor: '#444',
                                    color: 'white',
                                    borderRadius: '50px',
                                    px: isMobile ? 3 : 5,
                                    py: 1.5,
                                    fontWeight: 'bold',
                                    fontSize: isMobile ? '0.9rem' : '1.2rem',
                                    zIndex: 59,
                                    transition: 'all 0.4s cubic-bezier(0.15, 0.85, 0.25, 1)',
                                    '&:hover': { bgcolor: '#222' }
                                }}
                            >
                                {step === 0 ? "Noo..." : "It's not possible..."}
                            </Button>
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ValentinCard;